"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { createContext, useContext, useMemo, useState, useTransition } from "react";
import {
  quickUpdateArticle,
  removeHomepageSlot,
  updateHomepageSlotArticle,
} from "@/lib/admin-actions";
import { usePublicEditorialInlineEditor } from "@/components/site/public-editorial-inline-editor";
import type { PublicEditorArticle } from "@/lib/public-editorial-types";

export type EditorArticle = {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  coverImage: string;
  status: "DRAFT" | "PUBLISHED";
  categoryName: string;
};

type EditorContextValue = {
  enabled: boolean;
  articles: EditorArticle[];
};

const EditorContext = createContext<EditorContextValue | null>(null);

function useEditor() {
  const context = useContext(EditorContext);
  return context;
}

function publicEditorArticleToHomepageArticle(article: PublicEditorArticle): EditorArticle {
  return {
    id: article.id,
    title: article.title,
    slug: article.slug,
    excerpt: "",
    coverImage: article.coverImage,
    status: article.status,
    categoryName: article.categoryName,
  };
}

export function HomepageEditorProvider({
  articles,
  children,
}: {
  articles?: EditorArticle[];
  children: React.ReactNode;
}) {
  const inlineEditor = usePublicEditorialInlineEditor();
  const [enabled, setEnabled] = useState(false);
  const contextValue = inlineEditor
    ? {
        enabled: inlineEditor.enabled,
        articles: inlineEditor.articles.map(publicEditorArticleToHomepageArticle),
      }
    : { enabled, articles: articles ?? [] };

  return (
    <EditorContext.Provider value={contextValue}>
      {inlineEditor ? null : (
        <div className="fixed bottom-4 right-4 z-[90]">
          <label className="flex cursor-pointer items-center gap-3 rounded-lg border border-[#d8d2c5] bg-white px-4 py-3 text-sm font-black uppercase text-[#111111] shadow-sm">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(event) => setEnabled(event.target.checked)}
              className="h-4 w-4 accent-[#A97800]"
            />
            Szerkesztő mód
          </label>
        </div>
      )}
      {children}
    </EditorContext.Provider>
  );
}

export function EditableFrame({
  article,
  slotId,
  slotType,
  slotOrder,
  slotLabel,
  mode = "auto",
  children,
}: {
  article: EditorArticle;
  slotId?: string;
  slotType?: string;
  slotOrder?: number;
  slotLabel?: string;
  mode?: "auto" | "manual";
  children: React.ReactNode;
}) {
  const editor = useEditor();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [quickEditOpen, setQuickEditOpen] = useState(false);
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [actionTone, setActionTone] = useState<"success" | "error">("success");
  const [, startActionTransition] = useTransition();
  const router = useRouter();

  if (!editor?.enabled) {
    return <>{children}</>;
  }
  const { articles } = editor;
  const canAssignSlot = Boolean(slotId || slotType);

  return (
    <div className="group/editor relative w-full max-w-none rounded-lg outline outline-1 outline-dashed outline-[#A97800]/60 outline-offset-4">
      <details className="absolute right-1 top-1 z-[1000]">
        <summary className="flex min-h-7 cursor-pointer list-none items-center gap-1.5 rounded border border-[#d8d2c5] bg-white px-2 py-1 text-[10px] font-black uppercase leading-none text-[#111111] shadow-sm marker:hidden hover:border-[#A4243B] [&::-webkit-details-marker]:hidden">
          {slotLabel ? <span className="max-w-28 truncate">{slotLabel}</span> : <span>Cikk</span>}
          <span className={`rounded px-1.5 py-0.5 text-[9px] ${mode === "manual" ? "bg-[#273E47] text-white" : "bg-[#EEF0EA] text-[#273E47]"}`}>
            {mode === "manual" ? "Kézi" : "Auto"}
          </span>
          <span aria-hidden="true">•••</span>
        </summary>
        <div className="absolute right-0 top-full mt-1 flex w-36 flex-col overflow-hidden rounded border border-[#d8d2c5] bg-white text-[#111111] shadow-sm">
          <button
            type="button"
            onClick={() => setQuickEditOpen(true)}
            className="px-2.5 py-2 text-left text-[10px] font-black uppercase hover:bg-[#f7f5ef] hover:text-[#A97800]"
          >
            Gyors edit
          </button>
          <Link
            href={`/admin/articles/${article.id}/edit`}
            className="px-2.5 py-2 text-[10px] font-black uppercase hover:bg-[#f7f5ef] hover:text-[#A97800]"
          >
            Szerkesztés
          </Link>
          {canAssignSlot ? (
            <>
              <button
                type="button"
                onClick={() => setPickerOpen(true)}
                className="px-2.5 py-2 text-left text-[10px] font-black uppercase hover:bg-[#f7f5ef] hover:text-[#A97800]"
              >
                Cserélés
              </button>
              {slotId ? (
                <form
                  onSubmit={(event) => {
                    event.preventDefault();
                    if (!window.confirm("Biztosan kiveszed ezt a cikket a slotból?")) {
                      return;
                    }
                    const formData = new FormData(event.currentTarget);
                    setActionMessage(null);
                    startActionTransition(async () => {
                      try {
                        const result = await removeHomepageSlot(formData);
                        setActionTone(result.ok ? "success" : "error");
                        setActionMessage(result.message ?? (result.ok ? "Slotból kivéve." : "A művelet nem sikerült."));
                        if (result.ok) router.refresh();
                      } catch {
                        setActionTone("error");
                        setActionMessage("A művelet nem sikerült. Próbáld újra.");
                      }
                    });
                  }}
                >
                  <input type="hidden" name="slotId" value={slotId} />
                  <button className="w-full px-2.5 py-2 text-left text-[10px] font-black uppercase hover:bg-[#f7f5ef] hover:text-red-600">
                    Slotból ki
                  </button>
                </form>
              ) : null}
            </>
          ) : null}
        </div>
      </details>
      {actionMessage ? <p className={`mt-2 text-xs font-semibold ${actionTone === "success" ? "text-emerald-700" : "text-[#A4243B]"}`}>{actionMessage}</p> : null}
      {children}
      {canAssignSlot ? (
        <ArticlePicker
          articles={articles}
          open={pickerOpen}
          onClose={() => setPickerOpen(false)}
          slotId={slotId}
          slotType={slotType}
          slotOrder={slotOrder}
          currentArticleId={article.id}
        />
      ) : null}
      <QuickEditDrawer
        article={article}
        open={quickEditOpen}
        onClose={() => setQuickEditOpen(false)}
      />
    </div>
  );
}

function ArticlePicker({
  articles,
  open,
  onClose,
  slotId,
  slotType,
  slotOrder,
  currentArticleId,
}: {
  articles: EditorArticle[];
  open: boolean;
  onClose: () => void;
  slotId?: string;
  slotType?: string;
  slotOrder?: number;
  currentArticleId: string;
}) {
  const [query, setQuery] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pending, startTransition] = useTransition();
  const router = useRouter();
  const visibleArticles = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return articles;
    return articles.filter((article) =>
      `${article.categoryName} ${article.title}`.toLowerCase().includes(normalized),
    );
  }, [articles, query]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[1200] bg-black/40 p-4" role="dialog" aria-modal="true">
      <div className="ml-auto flex h-full w-full max-w-xl flex-col rounded-lg bg-white shadow-2xl">
        <div className="border-b border-zinc-200 p-4">
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-xl font-black uppercase">Cikk cseréje</h2>
            <button
              type="button"
              onClick={onClose}
              className="rounded border border-zinc-200 px-3 py-1.5 text-sm font-black uppercase hover:bg-zinc-100"
            >
              Bezárás
            </button>
          </div>
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Keresés cím vagy kategória alapján"
            className="mt-4 h-11 w-full rounded-lg border border-zinc-300 px-3 text-sm"
          />
        </div>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            const formData = new FormData(event.currentTarget);
            setError(null);
            startTransition(async () => {
              try {
                const result = await updateHomepageSlotArticle(formData);
                if (!result.ok) {
                  setError(result.message);
                  return;
                }
                router.refresh();
                onClose();
              } catch {
                setError("A mentés nem sikerült. Próbáld újra.");
              }
            });
          }}
          className="flex min-h-0 flex-1 flex-col"
        >
          {slotId ? <input type="hidden" name="slotId" value={slotId} /> : null}
          {slotType ? <input type="hidden" name="slotType" value={slotType} /> : null}
          {typeof slotOrder === "number" ? <input type="hidden" name="slotOrder" value={slotOrder} /> : null}
          <div className="min-h-0 flex-1 overflow-y-auto p-4">
            <div className="grid gap-2">
              {visibleArticles.map((article) => (
                <label
                  key={article.id}
                  className="flex cursor-pointer gap-3 rounded-lg border border-zinc-200 p-3 text-sm hover:border-[#A97800]"
                >
                  <input
                    type="radio"
                    name="articleId"
                    value={article.id}
                    defaultChecked={article.id === currentArticleId}
                    className="mt-1 accent-[#A97800]"
                  />
                  <span>
                    <span className="block text-xs font-black uppercase text-[#e5481d]">
                      {article.categoryName}
                    </span>
                    <span className="font-bold text-zinc-950">{article.title}</span>
                  </span>
                </label>
              ))}
            </div>
          </div>
          <div className="flex justify-end gap-2 border-t border-zinc-200 p-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-zinc-300 px-4 py-2 text-sm font-black uppercase"
            >
              Mégse
            </button>
            <button disabled={pending} className="rounded-lg bg-[#A97800] px-4 py-2 text-sm font-black uppercase text-white disabled:cursor-wait disabled:opacity-60">
              {pending ? "Mentés..." : "Mentés"}
            </button>
          </div>
          {error ? <p className="px-4 pb-4 text-sm font-semibold text-[#A4243B]">{error}</p> : null}
        </form>
      </div>
    </div>
  );
}

function QuickEditDrawer({
  article,
  open,
  onClose,
}: {
  article: EditorArticle;
  open: boolean;
  onClose: () => void;
}) {
  const [error, setError] = useState<string | null>(null);
  const [pending, startTransition] = useTransition();
  const router = useRouter();

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[1200] bg-black/40 p-4" role="dialog" aria-modal="true">
      <div className="ml-auto flex h-full w-full max-w-lg flex-col rounded-lg bg-white shadow-2xl">
        <div className="flex items-center justify-between gap-3 border-b border-zinc-200 p-4">
          <h2 className="text-xl font-black uppercase">Gyors szerkesztés</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-zinc-200 px-3 py-1.5 text-sm font-black uppercase hover:bg-zinc-100"
          >
            Bezárás
          </button>
        </div>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            const formData = new FormData(event.currentTarget);
            setError(null);
            startTransition(async () => {
              try {
                const result = await quickUpdateArticle(formData);
                if (!result.ok) {
                  setError(result.message);
                  return;
                }
                router.refresh();
                onClose();
              } catch {
                setError("A mentés nem sikerült. Próbáld újra.");
              }
            });
          }}
          className="grid gap-4 overflow-y-auto p-4"
        >
          <input type="hidden" name="articleId" value={article.id} />
          <label className="grid gap-1 text-sm font-bold">
            Cím
            <input
              name="title"
              defaultValue={article.title}
              className="h-11 rounded-lg border border-zinc-300 px-3"
            />
          </label>
          <label className="grid gap-1 text-sm font-bold">
            Lead
            <textarea
              name="excerpt"
              defaultValue={article.excerpt}
              rows={4}
              className="rounded-lg border border-zinc-300 p-3"
            />
          </label>
          <label className="grid gap-1 text-sm font-bold">
            Borítókép URL
            <input
              name="coverImage"
              defaultValue={article.coverImage}
              className="h-11 rounded-lg border border-zinc-300 px-3"
            />
          </label>
          <label className="grid gap-1 text-sm font-bold">
            Státusz
            <select
              name="status"
              defaultValue={article.status}
              className="h-11 rounded-lg border border-zinc-300 bg-white px-3"
            >
              <option value="PUBLISHED">PUBLISHED</option>
              <option value="DRAFT">DRAFT</option>
            </select>
          </label>
          <div className="flex justify-end gap-2 border-t border-zinc-200 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-zinc-300 px-4 py-2 text-sm font-black uppercase"
            >
              Mégse
            </button>
            <button disabled={pending} className="rounded-lg bg-[#A97800] px-4 py-2 text-sm font-black uppercase text-white disabled:cursor-wait disabled:opacity-60">
              {pending ? "Mentés..." : "Mentés"}
            </button>
          </div>
          {error ? <p className="text-sm font-semibold text-[#A4243B]">{error}</p> : null}
        </form>
      </div>
    </div>
  );
}
